import React from "react";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import { chunk } from "underscore";

import ImagePanel from "wxrecall/components/Image";

// Format upper wind data into a display string (e.g., "270 045 +05" or "000 000 -12" for calm)
export function formatWindData(direction, speed, temperature, isError, intlFormatMessage) {
  if (isError) {
    return intlFormatMessage({ id: "alpha.upperWind.error" });
  }

  if (speed === null) {
    return "";
  }

  let temperatureString = "   ";
  if (temperature !== null && temperature !== undefined) {
    const sign = temperature >= 0 ? "+" : "-";
    temperatureString = `${sign}${String(Math.abs(temperature)).padStart(2, "0")}`;
  }

  if (speed < 3) {
    return `000 000 ${temperatureString}`;
  }

  const directionString = direction !== null ? String(direction).padStart(3, "0") : "   ";
  const speedString = String(speed).padStart(3, "0");
  return `${directionString} ${speedString} ${temperatureString}`;
}

export function rowFormatter(state, rowInfo, column) {
  if (!rowInfo) return {};

  return {
    style: {
      background: rowInfo.original.hasError ? "#ff00002b" : ""
    }
  };
}

// Given a bulletin represented by 'bulletin', format the text of the bulletin,
// and set the `formattedBulletin` property
export function formatBulletin(bulletin, meta, intl) {
  let formattedbulletin = "";

  switch (bulletin.type) {
    case "upperwind":
      formattedbulletin = formatUpperWindBulletin(
        bulletin.location,
        bulletin.text,
        intl.formatMessage
      );
      break;
    case "image":
      formattedbulletin = formatImageBulletin(
        bulletin,
        meta.now,
        meta.networkManager
      );
      break;
    case "space_weather":
      formattedbulletin = bulletin.text
        .replace(/^(FNXX\d+)/, '\n$1')
        .replace(/SWX ADVISORY/, '\nSWX ADVISORY')
        .replace(/(DTG:|SWXC:|ADVISORY NR:|NR RPLC:|SWX EFFECT:|OBS SWX:)/g, '\n$1')
        .replace(/(FCST SWX \+\d+ HR:)/g, '\n$1')
        .replace(/(RMK:|NXT ADVISORY:)/g, '\n$1')
        .replace(/ {2,}/g, ' '); // Replace 2 or more spaces with a single space
      break;
    case "taf":
      formattedbulletin = formatTaf(bulletin.text);
      break;
    case "notam":
      let text;
      try {
        const parsedData = JSON.parse(bulletin.text);

        // Preserve the text WITH the Q line, and the french and english text
        // This is needed to set the filter text property in `normalizeData`
        bulletin.formattedBulletinWithQLine = parsedData.raw;

        if (meta.language === "default") {
          text = parsedData.raw;
        } else if (meta.language === "english") {
          text = parsedData.english || parsedData.raw;
        } else if (meta.language === "french") {
          text = parsedData.french || parsedData.raw;
        } else {
          console.error("Unexpected language choice", meta.language);
          text = parsedData.raw;
        }

        formattedbulletin = formatNotam(text, meta.displayNotamQField);
      } catch (e) {
        // This is probably a NOTAMJ, so just set it using the raw text
        formattedbulletin = bulletin.formattedBulletinWithQLine = bulletin.text;
      }
      break;
    default:
      formattedbulletin = bulletin.text;
  }
  bulletin.formattedBulletin = formattedbulletin;
  return formattedbulletin;
}

export function formatTaf(text) {
  return text
    .replace(
      // Remove extra whitespace
      /\s+/g,
      " "
    )
    .replace(
      // Add linebreak before main forecast
      /([0-9]{6}Z) /,
      "$1\n"
    )
    .replace(
      // Linebreaks
      / (FM[0-9]{4}|BECMG |TEMPO |PROB[0-9]|RMK |INTER |TXM?[0-9])/g,
      "\n$1"
    )
    .replace(
      // Indents
      /(BECMG |TEMPO |PROB[0-9]|INTER )/g,
      "  $1"
    );
}

function formatUpperWindAltitudes(altitudes, colWidth, intlFormatMessage) {
  let headerLine = "";
  let forecastLine = "";

  altitudes.forEach(([altitude, windDirection, windSpeed, temperature, isError]) => {
    const forecastText = formatWindData(windDirection, windSpeed, temperature, isError, intlFormatMessage);
    headerLine += centreText(String(altitude), colWidth);
    forecastLine += forecastText.padEnd(colWidth, " ");
  });

  return { header: headerLine, forecast: forecastLine };
}

function centreText(text, width) {
  return text
    .padStart((width - text.length) / 2 + text.length)
    .padEnd(width, "  ");
}

export function formatUpperWindBulletin(location, text, intlFormatMessage) {
  const [
    header,
    issue_site,
    issue_time,
    data_based_on,
    validity_at,
    for_start,
    for_end,
    max_wind_altitude,
    max_wind_direction,
    max_wind_speed,
    tropopause,
    altitudes
  ] = JSON.parse(text);

  const colWidth = 16;
  const headerWidth = 48;  // Width for centering the header text

  let bulletin = [
    centreText(`${header} ${issue_site} ${moment.utc(issue_time).format("DDHHmm")}`, headerWidth)
  ];

  if (data_based_on) {
    bulletin.push(
      centreText(location, headerWidth),
      `${intlFormatMessage({ id: "DATA BASED ON" })} ${moment.utc(data_based_on).format("DDHHmm[Z]")}`
    );
  }

  bulletin.push(
    `${intlFormatMessage({ id: "VALID" })} ${moment.utc(validity_at).format("DDHHmm[Z]")}`,
    `${intlFormatMessage({ id: "FOR USE" })} ${moment.utc(for_start).format("HHmm")}-${moment.utc(for_end).format("HHmm[Z]")}`
  );

  if (tropopause) {
    bulletin.push(`TROP ${tropopause}`);
  }

  if (max_wind_altitude) {
    bulletin.push(
      `MAXW ${max_wind_altitude} ${max_wind_direction} ${max_wind_speed}`
    );
  }
  const altitudeListList = chunk(altitudes.sort((a, b) => a[0] - b[0]), 5);

  altitudeListList.forEach((altitudeList, index) => {
    const { header, forecast } = formatUpperWindAltitudes(altitudeList, colWidth, intlFormatMessage);
    bulletin.push(`${header}`);
    bulletin.push(`${forecast}`);
  });

  return bulletin.join(" ");
}

export function formatImageBulletin(bulletin, now, networkManager) {
  if (bulletin.text) {
    const data = JSON.parse(bulletin.text);
    // replicate property names from source/react/src/workspace/network/image.js
    data.subGeography = data.sub_geography;
    data.subProduct = data.sub_product;
    return <ImagePanel data={data} now={now} networkManager={networkManager} />;
  } else {
    return <FormattedMessage id="no-image-available" />;
  }
}

export function formatNotam(bulletin, displayQ) {
  if (displayQ) {
    return bulletin;
  }

  // Replace the first and second lines of text (if the second starts with `Q)`)
  // with the first line of text
  let regex = /^(.*\r?\n)Q\).*\r?\n/;
  return bulletin.replace(regex, "$1");
}
